// dependencies
// charting components
import {
	BarElement,
	CategoryScale,
	Chart,
	Chart as ChartJS,
	ChartOptions,
	LineElement,
	LinearScale,
	PointElement,
	Title,
	Tooltip,
} from "chart.js";
import { startOfMonth } from "date-fns";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { Line, getElementAtEvent } from "react-chartjs-2";

import EmptyChart from "../../../../../../components/EmptyChart";
// components
import Spinner from "../../../../../../components/Spinner/QuarterCircle";
import { CardBody } from "../../../../components/Card";
import CohortSelect from "../../../../components/CohortSelect";
// hooks
import useNPSTrendlineChart from "../../../../components/ENPSTrendlineChart/useNPSTrendlineChart";

// store
import useStore from "../../../../store";

import { useCohorts, usePageSettings, useUpdatePageSettings } from "../../../../../../api/hooks";

import { ICohortModel } from "../../../../../../api/services";

// styles
import css from "./TrendLineCard.module.scss";

// register chartjs plugins
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip);

const MAX_COHORTS = 5;

const ENPSTrendLineChart = () => {
	const [selectedCohorts, setSelectedCohorts] = useState<ICohortModel[]>([]);
	const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>([]);
	const [filter, month, cohort, setMonth, setCohort] = useStore((state) => [
		state.filter,
		state.eNPS.month,
		state.eNPS.cohort,
		state.eNPS.setMonth,
		state.eNPS.setCohort,
	]);

	// chart ref
	const chartRef = useRef<ChartJS>();

	// api hooks
	const { isLoading: isCohortsLoading, data: cohortsData } = useCohorts();

	const pageSettingsOnSuccess = (onSuccessData) => {
		const newSelectedCohortIds = onSuccessData?.eNPS?.trendline?.selected_cohorts || [];
		const newSelectedCohorts = cohortsData?.cohorts.filter((c) => newSelectedCohortIds.includes(c._id)) || [];
		setSelectedCohorts(newSelectedCohorts);
		setSelectedCohortIds(newSelectedCohortIds);
	};

	const setPageSettings = useUpdatePageSettings(pageSettingsOnSuccess).mutate;

	// state for selected cohorts
	const {
		data: pageSettingsData,
		refetch: refetchPageSettings,
		isLoading: isPageSettingsLoading,
		isSuccess,
	} = usePageSettings();

	useEffect(() => {
		if (isSuccess) {
			pageSettingsOnSuccess(pageSettingsData);
		}
	}, [isPageSettingsLoading]);

	// clear selectedDataPoint value when filter changes
	useEffect(() => {
		setMonth(null);
	}, [filter.value]);

	// refetch page settings when cohorts have loaded
	useEffect(() => {
		if (!isCohortsLoading) {
			refetchPageSettings();
		}
	}, [isCohortsLoading]);

	// get data for line chart
	const { data, legendItems, options, dateRangeLabels, isLoading, indexOfSelectedMonth, indexOfSelectedCohort } =
		useNPSTrendlineChart(filter, month, cohort, selectedCohorts);

	let hasSufficientData = false;

	// check if chart has sufficient data
	if (data.datasets[0].data && data.datasets[0].data.length) {
		const dataSetData = data.datasets[0].data;
		// data exists, set sufficient to true
		if (dataSetData.length) {
			hasSufficientData = true;
		}
	}

	// handles clicking on a data point on the line chart
	const onClick = (event) => {
		let monthClicked;
		if (chartRef.current) {
			monthClicked = getElementAtEvent(chartRef.current, event)[0]?.index;

			if (typeof monthClicked === "number") {
				const dataSet = getElementAtEvent(chartRef.current, event)[0]?.datasetIndex;

				// if a cohort is selected, don't allow clicking on other dataset points
				if (cohort && dataSet !== indexOfSelectedCohort) {
					return;
				}

				if (monthClicked === indexOfSelectedMonth && dataSet === indexOfSelectedCohort) {
					setMonth(null);
					setCohort(null);
				} else {
					// format date label to be a date object for store
					// defaults to first day of the month (year-month-1)
					const formattedMonth = startOfMonth(new Date(`${dateRangeLabels[monthClicked].value}-1`));

					setMonth(formattedMonth);

					if (dataSet === 0) {
						setCohort("all");
					} else if (dataSet !== indexOfSelectedCohort) {
						const selectedCohort = selectedCohorts[dataSet - 1];
						setCohort(selectedCohort._id);
					}
				}
			}
		}
	};

	// handle selection of cohorts
	const handleAddCohort = (cohort: string) => {
		if (selectedCohorts.length < MAX_COHORTS) {
			setPageSettings({
				settings_path: "eNPS.trendline.selected_cohorts",
				settings_value: [...selectedCohortIds, cohort],
			});
		}
	};

	// handle removal of cohort
	const handleCohortRemove = (cohortID: string) => {
		setPageSettings({
			settings_path: "eNPS.trendline.selected_cohorts",
			settings_value: selectedCohortIds.filter((c) => c !== cohortID),
		});
		if (cohortID === cohort) {
			setCohort(null);
		}
	};

	const handleCohortSelect = (cohortID: string) => {
		if (cohortID === cohort) {
			setCohort(null);
		} else {
			setCohort(cohortID);
		}
	};

	return (
		<>
			<CardBody>
				{isLoading && (
					<div className={css.spinnerBox}>
						<Spinner />
					</div>
				)}

				{!isLoading && !hasSufficientData && <EmptyChart type="trendline" />}

				{!isLoading && hasSufficientData && (
					<div className={css.cardBody}>
						<div className={css.chartContainer}>
							<div className={css.lineChart}>
								<Line
									ref={chartRef as MutableRefObject<Chart<"line", any, unknown>>}
									options={options as ChartOptions<"line">}
									data={data}
									onClick={onClick}
								/>
							</div>

							<div className={css.legendContainer}>
								<CohortSelect
									selectedCohorts={selectedCohorts}
									handleAddCohort={handleAddCohort}
									handleCohortRemove={handleCohortRemove}
									handleCohortSelect={handleCohortSelect}
									legendItems={legendItems}
									maxCohorts={MAX_COHORTS}
									cohort={cohort}
								/>
							</div>
						</div>
					</div>
				)}
			</CardBody>
		</>
	);
};

export default ENPSTrendLineChart;
